import type Assistant from '../models/Assistant';
import type AssistantViewController from '../components/AssistantView/AssistantViewController';
import type AssistedEditor from './WritingAgent/AssistedEditor';
import type AssistedWhiteboard from './WhiteboardAgent/AssistedWhiteboard';

export type AgentSuggestions = {
  title: string;
  prompt: string;
  prefix?: string;
  icon?: string;
}[];
export type AgentParams = {
  projectId: RecordId | undefined;
  [key: string]: any;
};

type AgentContentComponent = React.ComponentType<{
  assistant: Assistant;
  mimeType: string;
  content: string;
}>;

export type AgentInstances = {
  assistedEditor?: AssistedEditor;
  assistedWhiteboard?: AssistedWhiteboard;
};

export type ContextType = keyof AgentInstances;

// Name of the default agent to fall back to if no agent is specified.
const DEFAULT_AGENT_NAME = 'DefaultAgent';

// Base class for all agents.
class Agent {
  // Name of the Ruby class implementing the agent on the server.
  static name = 'Override in sub-class';

  static publicName = 'Override in sub-class';

  constructor(
    public controller: AssistantViewController,
    public readonly agentParams: AgentParams,
    public readonly contexts: AgentInstances
  ) {
    // Auto-attach to assistedEditor if available
    if (this.contexts.assistedEditor) {
      this.contexts.assistedEditor?.attachToAssistantViewController(controller);
    }

    // Auto-attach to assistedWhiteboard if available
    this.contexts.assistedWhiteboard?.attachToAssistantViewController(
      controller
    );
  }

  name(): string {
    return (this.constructor as typeof Agent).name;
  }

  publicName(): string {
    return (this.constructor as typeof Agent).publicName;
  }

  static initialPrompt(): string | null {
    return null;
  }

  // Determine if the agent is currently available for use based on the available
  // agent params and instances.
  static available(
    agentParams: AgentParams,
    agentInstances: AgentInstances
  ): boolean {
    return true;
  }

  // Suggestions for prompts that will be shown on the suggestions panel. The
  // key is the text for the button the user will see. The value is the prompt
  // text that will be pre-filled into the prompt editor when the button is
  // clicked.
  getSuggestions(): AgentSuggestions {
    return [];
  }

  // Override this method to return a custom component for rendering a specific
  // mime type. Return nothing to get the default rendering.
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  componentForMimeType(
    assistant: Assistant,
    mimeType: string
  ): AgentContentComponent | void {
    // Intentionally empty - override in subclasses as needed
  }

  // Register an agent with the system so that it can be looked up by name.
  static register(agent: typeof Agent) {
    this.agentRegistry[agent.name] = agent;
  }

  // Internal use methods =================================================

  // The registry of all agents. This is used to look up agents by name.
  static agentRegistry: Record<string, typeof Agent> = {};

  // Look up an agent by name.
  static lookup(name: string): typeof Agent | undefined {
    return this.agentRegistry[name];
  }

  static getAllAgents(): (typeof Agent)[] {
    return Object.values(this.agentRegistry);
  }
}

export default Agent;
export { DEFAULT_AGENT_NAME };
